import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import MaskedAccountNumber from "@/app/utils/helpers/MaskedAccountNumber";
import type {
  PaymentsBulkFileTransaction,
  GxwpsBulkFileTransaction,
} from "@/app/types/bulkPaymentsPayroll";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import type { ReactNode } from "react";
import BulkUploadView from "../bulk-uploadApprove/BulkUploadView";
import StatusBadge from "@/app/components/common/badge/StatusBadge";
import type { BulkPaymentReviewCardListProps } from "../BulkUpload";
import CardListSkeleton from "../BulkPaymentPayrollReviewSkeleton/CardListSkeleton";
import { useAppRoutingType } from "@/app/hooks/useAppRoutingType";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import { t } from "i18next";

const BulkPaymentReviewCardList = ({
  allTransactions,
  validTransactions,
  invalidTransactions,
  pendingTransactions,
  allPayrollTransactions,
  validPayrollTransactions,
  invalidPayrollTransactions,
  reviewSummary,
  payrollReviewSummary,
  isPayroll,
  shouldShowTabs,
  shouldShowTransactionSummary,
  loading = false,
  fileType,
  onTabChange,
  tabLoading = false,
}: BulkPaymentReviewCardListProps) => {
  const { appRoutingType } = useAppRoutingType();

  const [activeTab, setActiveTab] = useState("all");
  const [selectedPayment, setSelectedPayment] =
    useState<PaymentsBulkFileTransaction | null>(null);
  const [selectedPayroll, setSelectedPayroll] =
    useState<GxwpsBulkFileTransaction | null>(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const normalizedFileType = String(fileType || "")
    .toUpperCase()
    .trim();

  // ============================================================================
  // isNonPayroll: true for "NON PAYROLL" bulktype OR "NON WPS" fileFormat.
  //
  // These files come through the payroll API (isPayroll=true) but contain
  // payment-style fields (beneAccountName, debitAmount etc.) - not payroll
  // fields (employeeUniqueId, fixedSalary). So card UI uses payment layout,
  // BUT the transaction details API must still use gxPayrollTransactionDetailsApi
  // because the records are stored in the payroll transaction table.
  //
  // Layout decision : shouldUsePayrollLayout (false for NON WPS/NON PAYROLL)
  // Details API     : isPayroll prop (true for NON WPS/NON PAYROLL)
  // ============================================================================

  const isNonPayroll =
    normalizedFileType === "NON PAYROLL" || normalizedFileType === "NON WPS";

  const shouldUsePayrollLayout = isPayroll && !isNonPayroll;
  const usesPayrollArrays = shouldUsePayrollLayout || isNonPayroll;

  const [transactionIdentifiers, setTransactionIdentifiers] = useState<{
    uniqueKey: number;
    statusId: string;
  } | null>(null);

  const getDisplayedPayments = () => {
    if (usesPayrollArrays) {
      switch (activeTab) {
        case "valid":
          return validPayrollTransactions;
        case "errors":
          return invalidPayrollTransactions;
        default:
          return allPayrollTransactions;
      }
    }

    switch (activeTab) {
      case "valid":
        return validTransactions;
      case "errors":
        return invalidTransactions;
      case "pending":
        return pendingTransactions ?? [];
      default:
        return allTransactions;
    }
  };

  const displayedPayments = getDisplayedPayments();

  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>(
    () => {
      if (displayedPayments.length > 0) {
        const firstId = usesPayrollArrays
          ? (displayedPayments[0] as GxwpsBulkFileTransaction).bulkUpdTransId ||
            (displayedPayments[0] as any).uniquekey
          : (displayedPayments[0] as PaymentsBulkFileTransaction)
              .bulkFileTransId || (displayedPayments[0] as any).uniquekey;
        return { [firstId]: true };
      }
      return {};
    },
  );

  const toggleCard = (id: number) => {
    setExpandedCards((prev) => {
      if (prev[id]) {
        return { [id]: false };
      }
      return { [id]: true };
    });
  };

  const getTransactionIdentifiers = (item: any) => {
    if (usesPayrollArrays) {
      return {
        uniqueKey: item.bulkUpdTransId || item.uniquekey,
        statusId: item.recordStatusCode || item.currentStatusCode || "",
      };
    }
    return {
      uniqueKey: item.bulkFileTransId || item.uniquekey,
      statusId: item.recordStatusCode || item.currentStatusCode || "",
    };
  };

  // // BEFORE:
  // const handleViewDetails = (item: any) => {
  //   const identifiers = getTransactionIdentifiers(item);
  //   setTransactionIdentifiers(identifiers);

  //   if (shouldUsePayrollLayout) {
  //     setSelectedPayroll(item);
  //     setSelectedPayment(null);
  //   } else if (isNonPayroll) {
  //     setSelectedPayroll(item as GxwpsBulkFileTransaction);
  //     setSelectedPayment(null);
  //   } else {
  //     setSelectedPayment(item);
  //     setSelectedPayroll(null);
  //   }
  //   setOpenDrawer(true);
  // };

  // AFTER:
  const handleViewDetails = (item: any) => {
    const identifiers = getTransactionIdentifiers(item);
    setTransactionIdentifiers(identifiers);

    if (shouldUsePayrollLayout) {
      // Pure PAYROLL --> payroll layout + payroll API
      setSelectedPayroll(item);
      setSelectedPayment(null);
    } else if (isNonPayroll) {
      // NON PAYROLL / NON WPS --> payment layout + payroll API
      // Put item in selectedPayment so BulkUploadView renders payment layout
      setSelectedPayment(item as PaymentsBulkFileTransaction);
      setSelectedPayroll(null);
    } else {
      // PAYMENT --> payment layout + payment API
      setSelectedPayment(item);
      setSelectedPayroll(null);
    }
    setOpenDrawer(true);
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setSelectedPayment(null);
    setSelectedPayroll(null);
    setTransactionIdentifiers(null);
  };

  const formatPayrollAmount = (
    amount: number | string | null | undefined,
  ): ReactNode => {
    if (amount === null || amount === undefined || amount === "") {
      return "--";
    }
    const numAmount = Number(amount);
    if (Number.isNaN(numAmount)) {
      return "--";
    }
    const currency =
      payrollReviewSummary?.wpsBulkFileTransactionsHDREntity?.paymentCurrency;
    if (!currency) {
      return "--";
    }
    return gxCurrencyFormat(numAmount, currency, undefined, {
      showSymbol: true,
      showCode: false,
    }).formatted;
  };

  const getTabCounts = () => {
    if (usesPayrollArrays) {
      return {
        all:
          payrollReviewSummary?.totalTxnCount ?? allPayrollTransactions.length,
        valid:
          payrollReviewSummary?.validTxnCount ??
          validPayrollTransactions.length,
        errors:
          payrollReviewSummary?.invalidTxnCount ??
          invalidPayrollTransactions.length,
        pending: payrollReviewSummary?.pendingTxnCount ?? 0,
      };
    }

    return {
      all: reviewSummary?.totalTxnCount ?? allTransactions.length,
      valid: reviewSummary?.validTxnCount ?? validTransactions.length,
      errors: reviewSummary?.invalidTxnCount ?? invalidTransactions.length,
      pending:
        reviewSummary?.pendingTxnCount ?? pendingTransactions?.length ?? 0,
    };
  };

  const tabCounts = getTabCounts();
  const allCount = usesPayrollArrays
    ? (payrollReviewSummary?.totalTxnCount ?? allPayrollTransactions.length)
    : (reviewSummary?.totalTxnCount ?? allTransactions.length);

  const shouldShowValidationLabels = () => {
    let jobStatusCode: number | undefined;
    if (usesPayrollArrays && payrollReviewSummary) {
      jobStatusCode = payrollReviewSummary.jobStatusCode;
    } else if (!usesPayrollArrays && reviewSummary) {
      jobStatusCode = reviewSummary.jobStatusCode;
    }
    const validationStatusCodes = [4510, 7510, 3456, 4585, 7585];
    return jobStatusCode
      ? validationStatusCodes.includes(jobStatusCode)
      : false;
  };

  const useValidationLabels = shouldShowValidationLabels();

  const getStatusLabel = (): { statusLabel: string; reasonLabel: string } => {
    if (useValidationLabels) {
      return {
        statusLabel: "Validation Status",
        reasonLabel: "Validation Reason",
      };
    }
    return {
      statusLabel: "Status",
      reasonLabel: "Reason",
    };
  };

  const getReasonText = (item: any): string => {
    if (useValidationLabels) {
      return item.validationReason || "--";
    }
    return item.rejectReason || "--";
  };

  const getReasonColor = (item: any): string => {
    const isValid =
      item.recordStatusCode === "4507" ||
      item.recordStatusCode === "7507" ||
      item.currentStatusCode === 4507 ||
      item.validationStatus === "Success";
    if (isValid) return "#16a34a";

    const isPending =
      item.validationStatus === "PENDING" ||
      item.recordStatusCode === "4506" ||
      item.currentStatusCode === 4506;
    if (isPending) return "#B08019";

    return "#dc2626";
  };

  useEffect(() => {
    if (displayedPayments.length > 0) {
      const firstId = usesPayrollArrays
        ? (displayedPayments[0] as GxwpsBulkFileTransaction).bulkUpdTransId ||
          (displayedPayments[0] as any).uniquekey
        : (displayedPayments[0] as PaymentsBulkFileTransaction)
            .bulkFileTransId || (displayedPayments[0] as any).uniquekey;
      setExpandedCards({ [firstId]: true });
    }
  }, [activeTab, displayedPayments, usesPayrollArrays]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  if (loading) {
    return <CardListSkeleton shouldShowTabs={shouldShowTabs} />;
  }

  return (
    <div>
      <div>
        {shouldShowTabs ? (
          <div className="gx-bpr-card-list-main-sec">
            <div
              className={`gx-bpr-card-list-top-tab-btn gx-p12-sb ${
                activeTab === "all"
                  ? "gx-bpr-card-list-top-tab-btn-active"
                  : "gx-bpr-card-list-top-tab-bg-wh"
              }`}
            >
              <button type="button" onClick={() => handleTabChange("all")}>
                <p className="gx-p12-sb">
                  {t("all")} ({tabCounts.all})
                </p>
              </button>
            </div>

            {appRoutingType === "CB" && (
              <div
                className={`gx-bpr-card-list-top-tab-btn gx-p12-sb ${
                  activeTab === "pending"
                    ? "gx-bpr-card-list-top-tab-btn-active"
                    : "gx-bpr-card-list-top-tab-bg-wh"
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleTabChange("pending")}
                >
                  <p className="gx-p12-sb">
                    {t("pending")} ({tabCounts.pending})
                  </p>
                </button>
              </div>
            )}

            <div
              className={`gx-bpr-card-list-top-tab-btn gx-p12-sb ${
                activeTab === "valid"
                  ? "gx-bpr-card-list-top-tab-btn-active"
                  : "gx-bpr-card-list-top-tab-bg-wh"
              }`}
            >
              <button type="button" onClick={() => handleTabChange("valid")}>
                <p className="gx-p12-sb">
                  {t("valid")} ({tabCounts.valid})
                </p>
              </button>
            </div>

            <div
              className={`gx-bpr-card-list-top-tab-btn gx-p12-sb ${
                activeTab === "errors"
                  ? "gx-bpr-card-list-top-tab-btn-active"
                  : "gx-bpr-card-list-top-tab-bg-wh"
              }`}
            >
              <button type="button" onClick={() => handleTabChange("errors")}>
                <p className="gx-p12-sb">
                  {t("errors")} ({tabCounts.errors})
                </p>
              </button>
            </div>
          </div>
        ) : (
          <div className="gx-bpr-card-list-main-sec">
            <div className="gx-bpr-card-list-all-trans-sec-full">
              <div className="gx-bpr-card-list-all-trans-cnt">
                <p className="gx-p18-b">
                  {t("all_transactions")} ({allCount})
                </p>
              </div>
            </div>
          </div>
        )}

        {tabLoading ? (
          <CardListSkeleton shouldShowTabs={shouldShowTabs} />
        ) : (
          <div className="gx-bpr-payment-cards-scrol-sec gx-bpr-payment-cards-sec">
            {displayedPayments.length === 0 ? (
              <div className="gx-bpr-empty-state-container">
                <NoDataFound message="No Records Found" />
              </div>
            ) : (
              displayedPayments.map((item: any) => {
                const itemId = usesPayrollArrays
                  ? item.bulkUpdTransId || item.uniquekey
                  : item.bulkFileTransId || item.uniquekey;

                return (
                  <div
                    key={itemId}
                    className="gx-bpr-payment-cards-sec-id gx-bpr-payment-cards"
                  >
                    <button
                      type="button"
                      className="gx-bpr-payment-cards-sec-id-toggle"
                      onClick={() => toggleCard(itemId)}
                    >
                      {!shouldUsePayrollLayout && (
                        <>
                          <div className="gx-bpr-payment-cards-sec-id-cd-hd">
                            <div className="gx-bpr-payment-cards-sec-id-cd-sec-to">
                              <span className="gx-bpr-payment-cards-sec-id-cd-p-i">
                                <p className="gx-p12">{t("to")}</p>
                              </span>
                              <span className="gx-bpr-payment-cards-sec-id-cd-p-ii">
                                <p className="gx-p14-b">
                                  {item.beneAccountName}
                                </p>
                              </span>
                            </div>
                            <div className="gx-bpr-payment-cards-sec-dropdrown">
                              {expandedCards[itemId] ? (
                                <ChevronDown size={20} />
                              ) : (
                                <ChevronUp size={20} />
                              )}
                            </div>
                          </div>

                          <div className="gx-bpr-payroll-cards-sec-id-cd-sec-to mt-1">
                            <span className="gx-bpr-payroll-cards-sec-id-cd-p-i">
                              <p className="gx-p12">{t("growx_reference_")}</p>
                            </span>
                            <span className="gx-bpr-payroll-cards-sec-id-cd-p-ii">
                              <p className="gx-p14-b">{item.gxChnNo || "--"}</p>
                            </span>
                          </div>

                          <div className="gx-bpr-payment-amount-sec">
                            <span className="gx-bpr-payment-amount-in-p-i">
                              <p className="gx-p12-m">{t("amount")}</p>
                            </span>
                            <span
                              className={`gx-p14-sb ${
                                item.debitAmount || item.beneAmount
                                  ? "gx-bpr-payment-amount-in-p-active"
                                  : "gx-bpr-payment-amount-in-p-red"
                              }`}
                            >
                              {
                                gxCurrencyFormat(
                                  item.debitAmount ?? item.beneAmount,
                                  item.debitCurrency ?? item.beneCurrency,
                                  undefined,
                                  { showSymbol: true, showCode: false },
                                ).formatted
                              }
                            </span>
                          </div>
                        </>
                      )}

                      {shouldUsePayrollLayout && (
                        <>
                          <div className="gx-bpr-payroll-cards-sec-id-cd-hd">
                            <div className="gx-bpr-payroll-cards-sec-id-cd-sec-to">
                              <span className="gx-bpr-payroll-cards-sec-id-cd-p-i">
                                <p className="gx-p12">{t("employee_id")}</p>
                              </span>
                              <span className="gx-bpr-payroll-cards-sec-id-cd-p-ii">
                                <p className="gx-p14-b">
                                  {item.employeeUniqueId}
                                </p>
                              </span>
                            </div>
                            <div className="gx-bpr-payment-cards-sec-dropdrown">
                              {expandedCards[itemId] ? (
                                <ChevronDown size={20} />
                              ) : (
                                <ChevronUp size={20} />
                              )}
                            </div>
                          </div>

                          <div className="gx-bpr-payroll-amount-sec">
                            <div>
                              <span className="gx-bpr-payroll-amount-in-p-i">
                                <p className="gx-p12-m">{t("fixed_pay")}</p>
                              </span>
                              <span
                                className={`gx-p14-sb ${
                                  item.fixedSalary
                                    ? "gx-bpr-payroll-amount-in-p-active"
                                    : "gx-bpr-payroll-amount-in-p-red"
                                }`}
                              >
                                {formatPayrollAmount(item.fixedSalary)}
                              </span>
                            </div>
                            <div>
                              <span className="gx-bpr-payroll-amount-in-p-i">
                                <p className="gx-p12-m">{t("variable_pay")}</p>
                              </span>
                              <span
                                className={`gx-p14-sb ${
                                  item.incVariableSalary
                                    ? "gx-bpr-payroll-amount-in-p-active"
                                    : "gx-bpr-payroll-amount-in-p-red"
                                }`}
                              >
                                {formatPayrollAmount(item.incVariableSalary)}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </button>

                    {!shouldUsePayrollLayout && expandedCards[itemId] && (
                      <div className="gx-bpr-p-a-bene-acc-no-sec">
                        <div className="gx-bpr-p-a-bene-acc-no-sec-sp">
                          <div className="gx-bpr-p-a-bene-acc-no-sec-fl">
                            <span className="gx-bpr-p-a-bene-acc-no">
                              <p className="gx-p12-m">
                                {t("bene_account_number_iban")}
                              </p>
                            </span>
                            <span>
                              {item.beneAccountNo ? (
                                <MaskedAccountNumber
                                  accountNumber={item.beneAccountNo}
                                  showToggle
                                  toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
                                />
                              ) : (
                                <p className="gx-p12-b gx-bpr-p-a-bene-acc-no-dt-red">
                                  {t("missing")}
                                </p>
                              )}
                            </span>
                          </div>
                          <div className="gx-bpr-payments-account-in-sec">
                            <span className="gx-bpr-payments-account-in-data-l">
                              <p className="gx-p12-m">
                                {t("beneficiary_bank_name")}
                              </p>
                            </span>
                            <span className="gx-bpr-payments-account-in-data-r">
                              <p className="gx-p12-b">
                                {item.beneBankName || "--"}
                              </p>
                            </span>
                          </div>
                          <div className="gx-bpr-payments-account-in-sec">
                            <span className="gx-bpr-payments-account-in-data-l">
                              <p className="gx-p12-m">
                                {t("purpose_of_payment")}
                              </p>
                            </span>
                            <span className="gx-bpr-payments-account-in-data-r">
                              <p className="gx-p12-b">
                                {item.purposeDesc || "--"}
                              </p>
                            </span>
                          </div>
                          <div className="gx-bpr-payments-account-in-sec">
                            <span className="gx-bpr-payments-account-in-data-l">
                              <p className="gx-p12-m">{t("payment_details")}</p>
                            </span>
                            <span className="gx-bpr-payments-account-in-data-r">
                              <p className="gx-p12-b">
                                {item.paymentDetails || "--"}
                              </p>
                            </span>
                          </div>
                          <div className="gx-bpr-payments-account-in-sec">
                            <span className="gx-bpr-payments-account-in-data-l">
                              <p className="gx-p12-m">
                                {t("payment_reference")}
                              </p>
                            </span>
                            <span className="gx-bpr-payments-account-in-data-r">
                              <p className="gx-p12-b">
                                {item.paymentReference || "--"}
                              </p>
                            </span>
                          </div>

                          <div className="gx-bpr-payments-account-in-sec">
                            <span className="gx-bpr-payments-account-in-data-l">
                              <p className="gx-p12-m">
                                {getStatusLabel().statusLabel}
                              </p>
                            </span>
                            <span className="gx-bpr-payments-account-in-data-r">
                              <StatusBadge
                                module="bulk"
                                statusCode={
                                  item.recordStatusCode ||
                                  item.currentStatusCode ||
                                  ""
                                }
                              />
                            </span>
                          </div>

                          {shouldShowTabs && (
                            <div className="gx-bpr-payments-account-in-sec">
                              <span className="gx-bpr-payments-account-in-data-l">
                                <p className="gx-p12-m">
                                  {getStatusLabel().reasonLabel}
                                </p>
                              </span>
                              <span className="gx-bpr-payments-account-in-data-r">
                                <p
                                  className="gx-p12-b"
                                  style={{ color: getReasonColor(item) }}
                                >
                                  {getReasonText(item)}
                                </p>
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="gx-bpr-pay-acc-view-all-det-btn">
                          <button
                            type="button"
                            onClick={() => handleViewDetails(item)}
                          >
                            <p className="gx-p12-sb">
                              {t("view_full_details_")}
                            </p>
                          </button>
                        </div>
                      </div>
                    )}

                    {shouldUsePayrollLayout && expandedCards[itemId] && (
                      <div className="gx-bpr-payroll-pd-main-sec">
                        <div className="gx-bpr-payroll-pd-inner-sec">
                          <div className="gx-bpr-payroll-sub-hd-sec">
                            <p className="gx-p12-b">{t("payment_details")}</p>
                          </div>
                          <div className="gx-bpr-payroll-pd-in-sec-fl">
                            <span className="gx-bpr-payroll-pd-in-cnt">
                              <p className="gx-p12-m">
                                {t("employee_account_number")}
                              </p>
                            </span>
                            <span>
                              {item.empAccountNo ? (
                                <MaskedAccountNumber
                                  accountNumber={item.empAccountNo}
                                  showToggle
                                  toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
                                />
                              ) : (
                                <p className="gx-p12-b gx-bpr-payroll-pd-in-cnt-red">
                                  {t("missing")}
                                </p>
                              )}
                            </span>
                          </div>
                          <div className="gx-bpr-payroll-pd-in-cnt-p-sec">
                            <span className="gx-bpr-payroll-pd-in-cnt-p-l">
                              <p className="gx-p12-m">
                                {t("agent_id_routing")}
                              </p>
                            </span>
                            <span className="gx-bpr-payroll-pd-in-cnt-p-r">
                              <p className="gx-p12-b">{item.agentId}</p>
                            </span>
                          </div>
                          <div className="gx-bpr-payroll-pd-in-cnt-p-sec">
                            <span className="gx-bpr-payroll-pd-in-cnt-p-l">
                              <p className="gx-p12-m">{t("pay_period")}</p>
                            </span>
                            <span className="gx-bpr-payroll-pd-in-cnt-p-r">
                              <p className="gx-p12-b">
                                {item.payStartDate} {t("to")} {item.payEndDate}
                              </p>
                            </span>
                          </div>
                          <div className="gx-bpr-payroll-pd-in-cnt-p-sec">
                            <span className="gx-bpr-payroll-pd-in-cnt-p-l">
                              <p className="gx-p12-m">{t("leave_days")}</p>
                            </span>
                            <span className="gx-bpr-payroll-pd-in-cnt-p-r">
                              <p className="gx-p12-b">
                                {item.leaveDays} {t("days")}
                              </p>
                            </span>
                          </div>

                          {!isPayroll && (
                            <div className="gx-bpr-payments-account-in-sec">
                              <span className="gx-bpr-payments-account-in-data-l">
                                <p className="gx-p12-m">
                                  {getStatusLabel().statusLabel}
                                </p>
                              </span>
                              <span className="gx-bpr-payments-account-in-data-r">
                                <StatusBadge
                                  module="bulk"
                                  statusCode={
                                    item.recordStatusCode ||
                                    item.currentStatusCode ||
                                    ""
                                  }
                                />
                              </span>
                            </div>
                          )}

                          {shouldShowTabs && (
                            <>
                              <div className="gx-bpr-payments-account-in-sec">
                                <span className="gx-bpr-payments-account-in-data-l">
                                  <p className="gx-p12-m">
                                    {getStatusLabel().statusLabel}
                                  </p>
                                </span>
                                <span className="gx-bpr-payments-account-in-data-r">
                                  <StatusBadge
                                    module="bulk"
                                    statusCode={
                                      item.recordStatusCode ||
                                      item.currentStatusCode ||
                                      ""
                                    }
                                  />
                                </span>
                              </div>
                              <div className="gx-bpr-payments-account-in-sec">
                                <span className="gx-bpr-payments-account-in-data-l">
                                  <p className="gx-p12-m">
                                    {getStatusLabel().reasonLabel}
                                  </p>
                                </span>
                                <span className="gx-bpr-payments-account-in-data-r">
                                  <p
                                    className="gx-p12-b"
                                    style={{ color: getReasonColor(item) }}
                                  >
                                    {getReasonText(item)}
                                  </p>
                                </span>
                              </div>
                            </>
                          )}

                          {shouldShowTransactionSummary && (
                            <div className="gx-bpr-payroll-pd-in-cnt-p-sec">
                              <span className="gx-bpr-payroll-pd-in-cnt-p-l">
                                <p className="gx-p12-m">
                                  {t("transaction_status")}
                                </p>
                              </span>
                              <span className="gx-bpr-payroll-pd-in-cnt-p-r">
                                <p className="gx-p12-b">
                                  <StatusBadge
                                    module="bulk"
                                    statusCode={
                                      item.recordStatusCode ||
                                      item.recordStatusDesc ||
                                      ""
                                    }
                                  />
                                </p>
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="gx-bpr-pay-acc-view-all-det-btn">
                          <button
                            type="button"
                            onClick={() => handleViewDetails(item)}
                            className="gx-p12-sb"
                          >
                            {t("view_full_details_")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      <DrawerNoFooter
        title={shouldUsePayrollLayout ? "Payroll" : "Payment"}
        subtitle={t("details")}
        openDrawer={openDrawer}
        onClose={handleClose}
      >
        {/* BEFORE: */}
        {/* <BulkUploadView
          paymentData={selectedPayment}
          payrollData={selectedPayroll}
          reviewSummary={reviewSummary}
          payrollReviewSummary={payrollReviewSummary}
          isPayroll={shouldUsePayrollLayout}
          transactionIdentifiers={transactionIdentifiers}
        /> */}

        {/* AFTER: */}
        <BulkUploadView
          paymentData={selectedPayment}
          payrollData={selectedPayroll}
          reviewSummary={reviewSummary}
          payrollReviewSummary={payrollReviewSummary}
          isPayroll={shouldUsePayrollLayout}
          usePayrollApi={shouldUsePayrollLayout || isNonPayroll}
          transactionIdentifiers={transactionIdentifiers}
        />
      </DrawerNoFooter>
    </div>
  );
};

export default BulkPaymentReviewCardList;

// import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
// import { ChevronDown, ChevronUp } from "lucide-react";
// import { useEffect, useState } from "react";
// import MaskedAccountNumber from "@/app/utils/helpers/MaskedAccountNumber";
// import type {
//   PaymentsBulkFileTransaction,
//   GxwpsBulkFileTransaction,
// } from "@/app/types/bulkPaymentsPayroll";
// import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
// import type { ReactNode } from "react";
// import BulkUploadView from "../bulk-uploadApprove/BulkUploadView";
// import StatusBadge from "@/app/components/common/badge/StatusBadge";
// import type { BulkPaymentReviewCardListProps } from "../BulkUpload";
// import CardListSkeleton from "../BulkPaymentPayrollReviewSkeleton/CardListSkeleton";
// import { useAppRoutingType } from "@/app/hooks/useAppRoutingType";
// import NoDataFound from "@/app/components/common/warning/NoDataFound";
// import { t } from "i18next";

// const BulkPaymentReviewCardList = ({
//   allTransactions,
//   validTransactions,
//   invalidTransactions,
//   pendingTransactions,
//   allPayrollTransactions,
//   validPayrollTransactions,
//   invalidPayrollTransactions,
//   reviewSummary,
//   payrollReviewSummary,
//   isPayroll,
//   shouldShowTabs,
//   shouldShowTransactionSummary,
//   loading = false,
//   fileType,
//   onTabChange,
//   tabLoading = false,
// }: BulkPaymentReviewCardListProps) => {
//   const { appRoutingType } = useAppRoutingType();

//   const [activeTab, setActiveTab] = useState("all");
//   const [selectedPayment, setSelectedPayment] =
//     useState<PaymentsBulkFileTransaction | null>(null);
//   const [selectedPayroll, setSelectedPayroll] =
//     useState<GxwpsBulkFileTransaction | null>(null);
//   const [openDrawer, setOpenDrawer] = useState(false);

//   const normalizedFileType = String(fileType || "")
//     .toUpperCase()
//     .trim();

//   // ============================================================================
//   // isNonPayroll: true for "NON PAYROLL" bulktype OR "NON WPS" fileFormat.
//   //
//   // These files come through the payroll API (isPayroll=true) but contain
//   // payment-style fields (beneAccountName, debitAmount etc.) - not payroll
//   // fields (employeeUniqueId, fixedSalary). So card UI uses payment layout,
//   // BUT the transaction details API must still use gxPayrollTransactionDetailsApi
//   // because the records are stored in the payroll transaction table.
//   //
//   // Layout decision : shouldUsePayrollLayout (false for NON WPS/NON PAYROLL)
//   // Details API     : isPayroll prop (true for NON WPS/NON PAYROLL)
//   // ============================================================================

//   const isNonPayroll =
//     normalizedFileType === "NON PAYROLL" || normalizedFileType === "NON WPS";

//   const shouldUsePayrollLayout = isPayroll && !isNonPayroll;
//   const usesPayrollArrays = shouldUsePayrollLayout || isNonPayroll;

//   const [transactionIdentifiers, setTransactionIdentifiers] = useState<{
//     uniqueKey: number;
//     statusId: string;
//   } | null>(null);

//   const getDisplayedPayments = () => {
//     if (usesPayrollArrays) {
//       switch (activeTab) {
//         case "valid":
//           return validPayrollTransactions;
//         case "errors":
//           return invalidPayrollTransactions;
//         default:
//           return allPayrollTransactions;
//       }
//     }

//     switch (activeTab) {
//       case "valid":
//         return validTransactions;
//       case "errors":
//         return invalidTransactions;
//       case "pending":
//         return pendingTransactions ?? [];
//       default:
//         return allTransactions;
//     }
//   };

//   const displayedPayments = getDisplayedPayments();

//   const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>(
//     () => {
//       if (displayedPayments.length > 0) {
//         const firstId = usesPayrollArrays
//           ? (displayedPayments[0] as GxwpsBulkFileTransaction).bulkUpdTransId ||
//             (displayedPayments[0] as any).uniquekey
//           : (displayedPayments[0] as PaymentsBulkFileTransaction)
//               .bulkFileTransId || (displayedPayments[0] as any).uniquekey;
//         return { [firstId]: true };
//       }
//       return {};
//     },
//   );

//   const toggleCard = (id: number) => {
//     setExpandedCards((prev) => {
//       if (prev[id]) {
//         return { [id]: false };
//       }
//       return { [id]: true };
//     });
//   };

//   const getTransactionIdentifiers = (item: any) => {
//     if (usesPayrollArrays) {
//       return {
//         uniqueKey: item.bulkUpdTransId || item.uniquekey,
//         statusId: item.recordStatusCode || item.currentStatusCode || "",
//       };
//     }
//     return {
//       uniqueKey: item.bulkFileTransId || item.uniquekey,
//       statusId: item.recordStatusCode || item.currentStatusCode || "",
//     };
//   };

//   const handleViewDetails = (item: any) => {
//     const identifiers = getTransactionIdentifiers(item);
//     setTransactionIdentifiers(identifiers);

//     if (shouldUsePayrollLayout) {
//       setSelectedPayroll(item);
//       setSelectedPayment(null);
//     } else if (isNonPayroll) {
//       setSelectedPayroll(item as GxwpsBulkFileTransaction);
//       setSelectedPayment(null);
//     } else {
//       setSelectedPayment(item);
//       setSelectedPayroll(null);
//     }
//     setOpenDrawer(true);
//   };

//   const handleClose = () => {
//     setOpenDrawer(false);
//     setSelectedPayment(null);
//     setSelectedPayroll(null);
//     setTransactionIdentifiers(null);
//   };

//   const formatPayrollAmount = (
//     amount: number | string | null | undefined,
//   ): ReactNode => {
//     if (amount === null || amount === undefined || amount === "") {
//       return "--";
//     }
//     const numAmount = Number(amount);
//     if (Number.isNaN(numAmount)) {
//       return "--";
//     }
//     const currency =
//       payrollReviewSummary?.wpsBulkFileTransactionsHDREntity?.paymentCurrency;
//     if (!currency) {
//       return "--";
//     }
//     return gxCurrencyFormat(numAmount, currency, undefined, {
//       showSymbol: true,
//       showCode: false,
//     }).formatted;
//   };

//   const getTabCounts = () => {
//     if (usesPayrollArrays) {
//       return {
//         all:
//           payrollReviewSummary?.totalTxnCount ?? allPayrollTransactions.length,
//         valid:
//           payrollReviewSummary?.validTxnCount ??
//           validPayrollTransactions.length,
//         errors:
//           payrollReviewSummary?.invalidTxnCount ??
//           invalidPayrollTransactions.length,
//         pending: payrollReviewSummary?.pendingTxnCount ?? 0,
//       };
//     }

//     return {
//       all: reviewSummary?.totalTxnCount ?? allTransactions.length,
//       valid: reviewSummary?.validTxnCount ?? validTransactions.length,
//       errors: reviewSummary?.invalidTxnCount ?? invalidTransactions.length,
//       pending:
//         reviewSummary?.pendingTxnCount ?? pendingTransactions?.length ?? 0,
//     };
//   };

//   const tabCounts = getTabCounts();
//   const allCount = usesPayrollArrays
//     ? (payrollReviewSummary?.totalTxnCount ?? allPayrollTransactions.length)
//     : (reviewSummary?.totalTxnCount ?? allTransactions.length);

//   const shouldShowValidationLabels = () => {
//     let jobStatusCode: number | undefined;
//     if (usesPayrollArrays && payrollReviewSummary) {
//       jobStatusCode = payrollReviewSummary.jobStatusCode;
//     } else if (!usesPayrollArrays && reviewSummary) {
//       jobStatusCode = reviewSummary.jobStatusCode;
//     }
//     const validationStatusCodes = [4510, 7510, 3456, 4585, 7585];
//     return jobStatusCode
//       ? validationStatusCodes.includes(jobStatusCode)
//       : false;
//   };

//   const useValidationLabels = shouldShowValidationLabels();

//   const getStatusLabel = (): { statusLabel: string; reasonLabel: string } => {
//     if (useValidationLabels) {
//       return {
//         statusLabel: "Validation Status",
//         reasonLabel: "Validation Reason",
//       };
//     }
//     return {
//       statusLabel: "Status",
//       reasonLabel: "Reason",
//     };
//   };

//   const getReasonText = (item: any): string => {
//     if (useValidationLabels) {
//       return item.validationReason || "--";
//     }
//     return item.rejectReason || "--";
//   };

//   const getReasonColor = (item: any): string => {
//     const isValid =
//       item.recordStatusCode === "4507" ||
//       item.recordStatusCode === "7507" ||
//       item.currentStatusCode === 4507 ||
//       item.validationStatus === "Success";
//     if (isValid) return "#16a34a";

//     const isPending =
//       item.validationStatus === "PENDING" ||
//       item.recordStatusCode === "4506" ||
//       item.currentStatusCode === 4506;
//     if (isPending) return "#B08019";

//     return "#dc2626";
//   };

//   useEffect(() => {
//     if (displayedPayments.length > 0) {
//       const firstId = usesPayrollArrays
//         ? (displayedPayments[0] as GxwpsBulkFileTransaction).bulkUpdTransId ||
//           (displayedPayments[0] as any).uniquekey
//         : (displayedPayments[0] as PaymentsBulkFileTransaction)
//             .bulkFileTransId || (displayedPayments[0] as any).uniquekey;
//       setExpandedCards({ [firstId]: true });
//     }
//   }, [activeTab, displayedPayments, usesPayrollArrays]);

//   const handleTabChange = (tab: string) => {
//     setActiveTab(tab);
//     if (onTabChange) {
//       onTabChange(tab);
//     }
//   };

//   if (loading) {
//     return <CardListSkeleton shouldShowTabs={shouldShowTabs} />;
//   }

//   return (
//     <div>
//       <div>
//         {shouldShowTabs ? (
//           <div className="gx-bpr-card-list-main-sec">
//             <div
//               className={`gx-bpr-card-list-top-tab-btn gx-p12-sb ${
//                 activeTab === "all"
//                   ? "gx-bpr-card-list-top-tab-btn-active"
//                   : "gx-bpr-card-list-top-tab-bg-wh"
//               }`}
//             >
//               <button type="button" onClick={() => handleTabChange("all")}>
//                 <p className="gx-p12-sb">
//                   {t("all")} ({tabCounts.all})
//                 </p>
//               </button>
//             </div>

//             {appRoutingType === "CB" && (
//               <div
//                 className={`gx-bpr-card-list-top-tab-btn gx-p12-sb ${
//                   activeTab === "pending"
//                     ? "gx-bpr-card-list-top-tab-btn-active"
//                     : "gx-bpr-card-list-top-tab-bg-wh"
//                 }`}
//               >
//                 <button
//                   type="button"
//                   onClick={() => handleTabChange("pending")}
//                 >
//                   <p className="gx-p12-sb">
//                     {t("pending")} ({tabCounts.pending})
//                   </p>
//                 </button>
//               </div>
//             )}

//             <div
//               className={`gx-bpr-card-list-top-tab-btn gx-p12-sb ${
//                 activeTab === "valid"
//                   ? "gx-bpr-card-list-top-tab-btn-active"
//                   : "gx-bpr-card-list-top-tab-bg-wh"
//               }`}
//             >
//               <button type="button" onClick={() => handleTabChange("valid")}>
//                 <p className="gx-p12-sb">
//                   {t("valid")} ({tabCounts.valid})
//                 </p>
//               </button>
//             </div>

//             <div
//               className={`gx-bpr-card-list-top-tab-btn gx-p12-sb ${
//                 activeTab === "errors"
//                   ? "gx-bpr-card-list-top-tab-btn-active"
//                   : "gx-bpr-card-list-top-tab-bg-wh"
//               }`}
//             >
//               <button type="button" onClick={() => handleTabChange("errors")}>
//                 <p className="gx-p12-sb">
//                   {t("errors")} ({tabCounts.errors})
//                 </p>
//               </button>
//             </div>
//           </div>
//         ) : (
//           <div className="gx-bpr-card-list-main-sec">
//             <div className="gx-bpr-card-list-all-trans-sec-full">
//               <div className="gx-bpr-card-list-all-trans-cnt">
//                 <p className="gx-p18-b">
//                   {t("all_transactions")} ({allCount})
//                 </p>
//               </div>
//             </div>
//           </div>
//         )}

//         {tabLoading ? (
//           <CardListSkeleton shouldShowTabs={shouldShowTabs} />
//         ) : (
//           <div className="gx-bpr-payment-cards-scrol-sec gx-bpr-payment-cards-sec">
//             {displayedPayments.length === 0 ? (
//               <div className="gx-bpr-empty-state-container">
//                 <NoDataFound message="No Records Found" />
//               </div>
//             ) : (
//               displayedPayments.map((item: any) => {
//                 const itemId = usesPayrollArrays
//                   ? item.bulkUpdTransId || item.uniquekey
//                   : item.bulkFileTransId || item.uniquekey;

//                 return (
//                   <div
//                     key={itemId}
//                     className="gx-bpr-payment-cards-sec-id gx-bpr-payment-cards"
//                   >
//                     <button
//                       type="button"
//                       className="gx-bpr-payment-cards-sec-id-toggle"
//                       onClick={() => toggleCard(itemId)}
//                     >
//                       {!shouldUsePayrollLayout && (
//                         <>
//                           <div className="gx-bpr-payment-cards-sec-id-cd-hd">
//                             <div className="gx-bpr-payment-cards-sec-id-cd-sec-to">
//                               <span className="gx-bpr-payment-cards-sec-id-cd-p-i">
//                                 <p className="gx-p12">{t("to")}</p>
//                               </span>
//                               <span className="gx-bpr-payment-cards-sec-id-cd-p-ii">
//                                 <p className="gx-p14-b">
//                                   {item.beneAccountName}
//                                 </p>
//                               </span>
//                             </div>
//                             <div className="gx-bpr-payment-cards-sec-dropdrown">
//                               {expandedCards[itemId] ? (
//                                 <ChevronDown size={20} />
//                               ) : (
//                                 <ChevronUp size={20} />
//                               )}
//                             </div>
//                           </div>

//                           <div className="gx-bpr-payroll-cards-sec-id-cd-sec-to mt-1">
//                             <span className="gx-bpr-payroll-cards-sec-id-cd-p-i">
//                               <p className="gx-p12">{t("growx_reference_")}</p>
//                             </span>
//                             <span className="gx-bpr-payroll-cards-sec-id-cd-p-ii">
//                               <p className="gx-p14-b">{item.gxChnNo || "--"}</p>
//                             </span>
//                           </div>

//                           <div className="gx-bpr-payment-amount-sec">
//                             <span className="gx-bpr-payment-amount-in-p-i">
//                               <p className="gx-p12-m">{t("amount")}</p>
//                             </span>
//                             <span
//                               className={`gx-p14-sb ${
//                                 item.debitAmount || item.beneAmount
//                                   ? "gx-bpr-payment-amount-in-p-active"
//                                   : "gx-bpr-payment-amount-in-p-red"
//                               }`}
//                             >
//                               {
//                                 gxCurrencyFormat(
//                                   item.debitAmount ?? item.beneAmount,
//                                   item.debitCurrency ?? item.beneCurrency,
//                                   undefined,
//                                   { showSymbol: true, showCode: false },
//                                 ).formatted
//                               }
//                             </span>
//                           </div>
//                         </>
//                       )}

//                       {shouldUsePayrollLayout && (
//                         <>
//                           <div className="gx-bpr-payroll-cards-sec-id-cd-hd">
//                             <div className="gx-bpr-payroll-cards-sec-id-cd-sec-to">
//                               <span className="gx-bpr-payroll-cards-sec-id-cd-p-i">
//                                 <p className="gx-p12">{t("employee_id")}</p>
//                               </span>
//                               <span className="gx-bpr-payroll-cards-sec-id-cd-p-ii">
//                                 <p className="gx-p14-b">
//                                   {item.employeeUniqueId}
//                                 </p>
//                               </span>
//                             </div>
//                             <div className="gx-bpr-payment-cards-sec-dropdrown">
//                               {expandedCards[itemId] ? (
//                                 <ChevronDown size={20} />
//                               ) : (
//                                 <ChevronUp size={20} />
//                               )}
//                             </div>
//                           </div>

//                           <div className="gx-bpr-payroll-amount-sec">
//                             <div>
//                               <span className="gx-bpr-payroll-amount-in-p-i">
//                                 <p className="gx-p12-m">{t("fixed_pay")}</p>
//                               </span>
//                               <span
//                                 className={`gx-p14-sb ${
//                                   item.fixedSalary
//                                     ? "gx-bpr-payroll-amount-in-p-active"
//                                     : "gx-bpr-payroll-amount-in-p-red"
//                                 }`}
//                               >
//                                 {formatPayrollAmount(item.fixedSalary)}
//                               </span>
//                             </div>
//                             <div>
//                               <span className="gx-bpr-payroll-amount-in-p-i">
//                                 <p className="gx-p12-m">{t("variable_pay")}</p>
//                               </span>
//                               <span
//                                 className={`gx-p14-sb ${
//                                   item.incVariableSalary
//                                     ? "gx-bpr-payroll-amount-in-p-active"
//                                     : "gx-bpr-payroll-amount-in-p-red"
//                                 }`}
//                               >
//                                 {formatPayrollAmount(item.incVariableSalary)}
//                               </span>
//                             </div>
//                           </div>
//                         </>
//                       )}
//                     </button>

//                     {!shouldUsePayrollLayout && expandedCards[itemId] && (
//                       <div className="gx-bpr-p-a-bene-acc-no-sec">
//                         <div className="gx-bpr-p-a-bene-acc-no-sec-sp">
//                           <div className="gx-bpr-p-a-bene-acc-no-sec-fl">
//                             <span className="gx-bpr-p-a-bene-acc-no">
//                               <p className="gx-p12-m">
//                                 {t("bene_account_number_iban")}
//                               </p>
//                             </span>
//                             <span>
//                               {item.beneAccountNo ? (
//                                 <MaskedAccountNumber
//                                   accountNumber={item.beneAccountNo}
//                                   showToggle
//                                   toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
//                                 />
//                               ) : (
//                                 <p className="gx-p12-b gx-bpr-p-a-bene-acc-no-dt-red">
//                                   {t("missing")}
//                                 </p>
//                               )}
//                             </span>
//                           </div>
//                           <div className="gx-bpr-payments-account-in-sec">
//                             <span className="gx-bpr-payments-account-in-data-l">
//                               <p className="gx-p12-m">
//                                 {t("beneficiary_bank_name")}
//                               </p>
//                             </span>
//                             <span className="gx-bpr-payments-account-in-data-r">
//                               <p className="gx-p12-b">
//                                 {item.beneBankName || "--"}
//                               </p>
//                             </span>
//                           </div>
//                           <div className="gx-bpr-payments-account-in-sec">
//                             <span className="gx-bpr-payments-account-in-data-l">
//                               <p className="gx-p12-m">
//                                 {t("purpose_of_payment")}
//                               </p>
//                             </span>
//                             <span className="gx-bpr-payments-account-in-data-r">
//                               <p className="gx-p12-b">
//                                 {item.purposeDesc || "--"}
//                               </p>
//                             </span>
//                           </div>
//                           <div className="gx-bpr-payments-account-in-sec">
//                             <span className="gx-bpr-payments-account-in-data-l">
//                               <p className="gx-p12-m">{t("payment_details")}</p>
//                             </span>
//                             <span className="gx-bpr-payments-account-in-data-r">
//                               <p className="gx-p12-b">
//                                 {item.paymentDetails || "--"}
//                               </p>
//                             </span>
//                           </div>
//                           <div className="gx-bpr-payments-account-in-sec">
//                             <span className="gx-bpr-payments-account-in-data-l">
//                               <p className="gx-p12-m">
//                                 {t("payment_reference")}
//                               </p>
//                             </span>
//                             <span className="gx-bpr-payments-account-in-data-r">
//                               <p className="gx-p12-b">
//                                 {item.paymentReference || "--"}
//                               </p>
//                             </span>
//                           </div>

//                           <div className="gx-bpr-payments-account-in-sec">
//                             <span className="gx-bpr-payments-account-in-data-l">
//                               <p className="gx-p12-m">
//                                 {getStatusLabel().statusLabel}
//                               </p>
//                             </span>
//                             <span className="gx-bpr-payments-account-in-data-r">
//                               <StatusBadge
//                                 module="bulk"
//                                 statusCode={
//                                   item.recordStatusCode ||
//                                   item.currentStatusCode ||
//                                   ""
//                                 }
//                               />
//                             </span>
//                           </div>

//                           {shouldShowTabs && (
//                             <div className="gx-bpr-payments-account-in-sec">
//                               <span className="gx-bpr-payments-account-in-data-l">
//                                 <p className="gx-p12-m">
//                                   {getStatusLabel().reasonLabel}
//                                 </p>
//                               </span>
//                               <span className="gx-bpr-payments-account-in-data-r">
//                                 <p
//                                   className="gx-p12-b"
//                                   style={{ color: getReasonColor(item) }}
//                                 >
//                                   {getReasonText(item)}
//                                 </p>
//                               </span>
//                             </div>
//                           )}
//                         </div>

//                         <div className="gx-bpr-pay-acc-view-all-det-btn">
//                           <button
//                             type="button"
//                             onClick={() => handleViewDetails(item)}
//                           >
//                             <p className="gx-p12-sb">
//                               {t("view_full_details_")}
//                             </p>
//                           </button>
//                         </div>
//                       </div>
//                     )}

//                     {shouldUsePayrollLayout && expandedCards[itemId] && (
//                       <div className="gx-bpr-payroll-pd-main-sec">
//                         <div className="gx-bpr-payroll-pd-inner-sec">
//                           <div className="gx-bpr-payroll-sub-hd-sec">
//                             <p className="gx-p12-b">{t("payment_details")}</p>
//                           </div>
//                           <div className="gx-bpr-payroll-pd-in-sec-fl">
//                             <span className="gx-bpr-payroll-pd-in-cnt">
//                               <p className="gx-p12-m">
//                                 {t("employee_account_number")}
//                               </p>
//                             </span>
//                             <span>
//                               {item.empAccountNo ? (
//                                 <MaskedAccountNumber
//                                   accountNumber={item.empAccountNo}
//                                   showToggle
//                                   toggleIconClassName="w-3 h-3 cursor-pointer opacity-70 hover:opacity-100"
//                                 />
//                               ) : (
//                                 <p className="gx-p12-b gx-bpr-payroll-pd-in-cnt-red">
//                                   {t("missing")}
//                                 </p>
//                               )}
//                             </span>
//                           </div>
//                           <div className="gx-bpr-payroll-pd-in-cnt-p-sec">
//                             <span className="gx-bpr-payroll-pd-in-cnt-p-l">
//                               <p className="gx-p12-m">
//                                 {t("agent_id_routing")}
//                               </p>
//                             </span>
//                             <span className="gx-bpr-payroll-pd-in-cnt-p-r">
//                               <p className="gx-p12-b">{item.agentId}</p>
//                             </span>
//                           </div>
//                           <div className="gx-bpr-payroll-pd-in-cnt-p-sec">
//                             <span className="gx-bpr-payroll-pd-in-cnt-p-l">
//                               <p className="gx-p12-m">{t("pay_period")}</p>
//                             </span>
//                             <span className="gx-bpr-payroll-pd-in-cnt-p-r">
//                               <p className="gx-p12-b">
//                                 {item.payStartDate} {t("to")} {item.payEndDate}
//                               </p>
//                             </span>
//                           </div>
//                           <div className="gx-bpr-payroll-pd-in-cnt-p-sec">
//                             <span className="gx-bpr-payroll-pd-in-cnt-p-l">
//                               <p className="gx-p12-m">{t("leave_days")}</p>
//                             </span>
//                             <span className="gx-bpr-payroll-pd-in-cnt-p-r">
//                               <p className="gx-p12-b">
//                                 {item.leaveDays} {t("days")}
//                               </p>
//                             </span>
//                           </div>

//                           {!isPayroll && (
//                             <div className="gx-bpr-payments-account-in-sec">
//                               <span className="gx-bpr-payments-account-in-data-l">
//                                 <p className="gx-p12-m">
//                                   {getStatusLabel().statusLabel}
//                                 </p>
//                               </span>
//                               <span className="gx-bpr-payments-account-in-data-r">
//                                 <StatusBadge
//                                   module="bulk"
//                                   statusCode={
//                                     item.recordStatusCode ||
//                                     item.currentStatusCode ||
//                                     ""
//                                   }
//                                 />
//                               </span>
//                             </div>
//                           )}

//                           {shouldShowTabs && (
//                             <>
//                               <div className="gx-bpr-payments-account-in-sec">
//                                 <span className="gx-bpr-payments-account-in-data-l">
//                                   <p className="gx-p12-m">
//                                     {getStatusLabel().statusLabel}
//                                   </p>
//                                 </span>
//                                 <span className="gx-bpr-payments-account-in-data-r">
//                                   <StatusBadge
//                                     module="bulk"
//                                     statusCode={
//                                       item.recordStatusCode ||
//                                       item.currentStatusCode ||
//                                       ""
//                                     }
//                                   />
//                                 </span>
//                               </div>
//                               <div className="gx-bpr-payments-account-in-sec">
//                                 <span className="gx-bpr-payments-account-in-data-l">
//                                   <p className="gx-p12-m">
//                                     {getStatusLabel().reasonLabel}
//                                   </p>
//                                 </span>
//                                 <span className="gx-bpr-payments-account-in-data-r">
//                                   <p
//                                     className="gx-p12-b"
//                                     style={{ color: getReasonColor(item) }}
//                                   >
//                                     {getReasonText(item)}
//                                   </p>
//                                 </span>
//                               </div>
//                             </>
//                           )}

//                           {shouldShowTransactionSummary && (
//                             <div className="gx-bpr-payroll-pd-in-cnt-p-sec">
//                               <span className="gx-bpr-payroll-pd-in-cnt-p-l">
//                                 <p className="gx-p12-m">
//                                   {t("transaction_status")}
//                                 </p>
//                               </span>
//                               <span className="gx-bpr-payroll-pd-in-cnt-p-r">
//                                 <p className="gx-p12-b">
//                                   <StatusBadge
//                                     module="bulk"
//                                     statusCode={
//                                       item.recordStatusCode ||
//                                       item.recordStatusDesc ||
//                                       ""
//                                     }
//                                   />
//                                 </p>
//                               </span>
//                             </div>
//                           )}
//                         </div>

//                         <div className="gx-bpr-pay-acc-view-all-det-btn">
//                           <button
//                             type="button"
//                             onClick={() => handleViewDetails(item)}
//                             className="gx-p12-sb"
//                           >
//                             {t("view_full_details_")}
//                           </button>
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 );
//               })
//             )}
//           </div>
//         )}
//       </div>

//       <DrawerNoFooter
//         title={shouldUsePayrollLayout ? "Payroll" : "Payment"}
//         subtitle={t("details")}
//         openDrawer={openDrawer}
//         onClose={handleClose}
//       >
//         <BulkUploadView
//           paymentData={selectedPayment}
//           payrollData={selectedPayroll}
//           reviewSummary={reviewSummary}
//           payrollReviewSummary={payrollReviewSummary}
//           isPayroll={isPayroll}
//           transactionIdentifiers={transactionIdentifiers}
//         />
//       </DrawerNoFooter>
//     </div>
//   );
// };

// export default BulkPaymentReviewCardList;
